import type {
  ActivityRecommendedActionKind,
  ActivitySubjectCategory,
  ActivitySubjectCard,
} from '@shared/types/activity-feed';

export const formatRelativeTime = (iso: string | null): string => {
  if (!iso) return 'Date inconnue';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Date inconnue';

  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / (1000 * 60));
  const formatter = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });

  if (Math.abs(diffMinutes) < 60) {
    return formatter.format(diffMinutes, 'minute');
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 48) {
    return formatter.format(diffHours, 'hour');
  }

  const diffDays = Math.round(diffHours / 24);
  if (Math.abs(diffDays) < 14) {
    return formatter.format(diffDays, 'day');
  }

  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });
};

export const formatActivityAge = (iso: string | null): string => {
  if (!iso) return 'Date inconnue';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Date inconnue';

  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.max(0, Math.round(diffMs / (1000 * 60)));

  if (diffMinutes < 60) {
    return `il y a ${Math.max(1, diffMinutes)} min`;
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 48) {
    return `il y a ${diffHours} h`;
  }

  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 60) {
    return diffDays === 1 ? 'hier' : `il y a ${diffDays} j`;
  }

  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });
};

export const formatAbsoluteDateTime = (iso: string | null): string => {
  if (!iso) return 'Date inconnue';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Date inconnue';

  return date.toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getActivityAgeHours = (iso: string | null): number | null => {
  if (!iso) return null;

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  return (Date.now() - date.getTime()) / (1000 * 60 * 60);
};

export const CATEGORY_LABELS: Record<ActivitySubjectCategory, string> = {
  followups: 'Follow-ups',
  meetings: 'Meetings',
  crm: 'CRM',
  drafts: 'Brouillons',
  general: 'Comptes',
};

export type ActivityRegisterSubjectType =
  'account' | 'deal' | 'meeting' | 'contact' | 'run';
export type ActivityRegisterState =
  'open' | 'in_progress' | 'waiting' | 'at_risk' | 'error' | 'done';
export type ActivityRegisterSuiteTone = 'primary' | 'secondary' | 'danger' | 'link';

export const RECOMMENDED_ACTION_KIND_LABELS: Record<
  ActivityRecommendedActionKind,
  string
> = {
  follow_up: 'Follow-up',
  meeting_prep: 'Meeting',
  diagnostic: 'Diagnostic',
  research: 'Recherche',
  crm_update: 'CRM',
  personalized_email: 'Email personnalisé',
  email: 'Email',
  run_review: 'Run',
  other: 'Autre',
};

export const STATE_LABELS: Record<ActivityRegisterState, string> = {
  open: 'Action ouverte',
  in_progress: 'En cours',
  waiting: 'En attente',
  at_risk: 'À risque',
  error: 'Erreur',
  done: 'Terminé',
};

export const STATE_STYLES: Record<
  ActivityRegisterState,
  {
    dot: string;
    badge: string;
    rowDot: string;
  }
> = {
  open: {
    dot: 'bg-domino-aubergine',
    badge: 'border-domino-cardBorder bg-domino-lilac text-domino-aubergine',
    rowDot: 'bg-domino-aubergine',
  },
  in_progress: {
    dot: 'bg-domino-prune',
    badge: 'border-domino-cardBorder bg-domino-lilac text-domino-aubergine',
    rowDot: 'bg-domino-prune',
  },
  waiting: {
    dot: 'bg-slate-400',
    badge: 'border-slate-200 bg-slate-100 text-slate-700',
    rowDot: 'bg-slate-400',
  },
  at_risk: {
    dot: 'bg-amber-500',
    badge: 'border-amber-200 bg-amber-50 text-amber-700',
    rowDot: 'bg-amber-500',
  },
  error: {
    dot: 'bg-red-500',
    badge: 'border-red-200 bg-red-50 text-red-700',
    rowDot: 'bg-red-500',
  },
  done: {
    dot: 'bg-emerald-500',
    badge: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    rowDot: 'bg-emerald-500',
  },
};

const normalizeActivityText = (value: string | null | undefined): string =>
  typeof value === 'string'
    ? value
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
    : '';

const buildSubjectSearchText = (subject: ActivitySubjectCard): string =>
  [
    subject.subjectKind,
    subject.subjectLabel,
    subject.subjectSubtitle,
    subject.category,
    subject.headline,
    subject.nextStepLabel,
    ...subject.chips.map((chip) => chip.label),
  ]
    .filter((value): value is string => typeof value === 'string')
    .map(normalizeActivityText)
    .join(' ');

export const deriveActivitySubjectType = (
  subject: ActivitySubjectCard
): ActivityRegisterSubjectType => {
  const kind = normalizeActivityText(subject.subjectKind);
  const text = buildSubjectSearchText(subject);

  if (kind === 'deal' || text.includes(' deal ') || text.includes('pipeline'))
    return 'account';
  if (
    kind === 'meeting' ||
    kind === 'call' ||
    text.includes('meeting') ||
    text.includes('call')
  )
    return 'meeting';
  if (kind === 'contact' || kind === 'email' || text.includes('contact'))
    return 'contact';
  if (kind === 'company' || text.includes('compte') || text.includes('account'))
    return 'account';
  if (text.includes('run') || text.includes('execution')) return 'run';

  return 'account';
};

export const deriveActivityRegisterState = (
  subject: ActivitySubjectCard
): ActivityRegisterState => {
  const text = buildSubjectSearchText(subject);
  const ageHours = getActivityAgeHours(subject.latestEventAt);
  const subjectType = deriveActivitySubjectType(subject);

  if (
    text.includes('erreur') ||
    text.includes('echec') ||
    text.includes('failed') ||
    text.includes('bloque')
  ) {
    return 'error';
  }

  if (
    subjectType === 'deal' &&
    (subject.severity === 'critical' ||
      text.includes('risque') ||
      text.includes('stagnant') ||
      text.includes('silencieux'))
  ) {
    return 'at_risk';
  }

  if (subject.counts.open > 0) {
    return 'open';
  }

  if (
    subject.counts.exactTriggered > 0 ||
    text.includes('lance') ||
    text.includes('en cours')
  ) {
    return 'in_progress';
  }

  if (
    text.includes('attente') ||
    text.includes('aucune reponse') ||
    text.includes('reponse') ||
    (ageHours !== null && ageHours >= 24 * 7 && subject.counts.exactDone === 0)
  ) {
    return 'waiting';
  }

  if (subject.severity === 'critical') {
    return 'at_risk';
  }

  return 'done';
};

export const deriveActivityDisplayEntity = (
  subject: ActivitySubjectCard
): {
  label: string;
  context: string | null;
} => {
  const subjectType = deriveActivitySubjectType(subject);
  const label = subject.subjectLabel.trim();
  const subtitle = subject.subjectSubtitle?.trim() || null;
  const callMatch = label.match(/^call\s*[·-]\s*(.+?)(?:\s*\((.+)\))?$/i);

  if (callMatch?.[1]) {
    return {
      label: callMatch[1].trim(),
      context: 'Meeting',
    };
  }

  if (subjectType === 'deal') {
    const [company, ...dealParts] = label
      .split('·')
      .map((part) => part.trim())
      .filter(Boolean);
    if (company && dealParts.length > 0) {
      return {
        label: company,
        context: dealParts.join(' · '),
      };
    }
  }

  if (subjectType === 'run') {
    return {
      label,
      context: 'Run',
    };
  }

  return {
    label,
    context: subtitle,
  };
};

export const deriveActivityNextActionLabel = (subject: ActivitySubjectCard): string => {
  if (subject.nextStepAction?.label) return subject.nextStepAction.label;
  if (subject.nextStepLabel) return subject.nextStepLabel;

  const state = deriveActivityRegisterState(subject);
  if (state === 'error') return "Voir l'erreur";
  if (state === 'at_risk') return 'Voir le diagnostic';

  return 'Voir le suivi';
};

export const getActivityPriorityRank = (subject: ActivitySubjectCard): number => {
  const stateRank: Record<ActivityRegisterState, number> = {
    error: 0,
    open: 1,
    at_risk: 2,
    in_progress: 3,
    waiting: 4,
    done: 5,
  };

  return stateRank[deriveActivityRegisterState(subject)];
};
